import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Sparkles, Info, Filter, ChevronRight, AlertTriangle, MapPin, Users, CheckCircle2, Clock, AlertCircle, ChevronLeft, ChevronDown, Maximize2, Minimize2, Target, X, Calendar, Eye, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjectFilter } from '../contexts/ProjectFilterContext';
import { useContractorFilter } from '../contexts/ContractorFilterContext';
import { RiskAreaDetailModal } from './RiskAreaDetailModal';
import { InsightsTab } from './InsightsTab';
import { KPIsTab } from './KPIsTab';
import { RiskMethodologyModal } from './RiskMethodologyModal';
import { useKPIs } from '../contexts/KPIContext';
import { ENRICHED_MOCK_RISK_AREAS } from '../data/mockPreventionData';
import { EnrichedRiskArea } from '../types/prevention';
import { VariantATable } from './prevention/VariantATable';
import { VariantBList } from './prevention/VariantBList';
import { VariantCCards } from './prevention/VariantCCards';
import { VariantDSplit } from './prevention/VariantDSplit';
import { VariantEKanban } from './prevention/VariantEKanban';
import { Settings2 } from 'lucide-react';
export interface RiskAreasWidgetProps {
  fullPage?: boolean;
  initialFilter?: 'sif' | 'open' | null;
  projectFilter?: string | null;
  onClearProjectFilter?: () => void;
}
export type RiskLevel = 'critical' | 'high' | 'medium' | 'low';
export type RiskStatus = 'open' | 'measures_taken' | 'closed' | 'monitoring';
export interface InexperiencedWorker {
  id: string;
  name: string;
  contractor: string;
  phone: string;
  firstDayOnSite: Date;
  daysOnSite: number;
}
export interface NearMissReport {
  id: string;
  title: string;
  description: string;
  reportedBy: string;
  reportedAt: Date;
  location: string;
  severity: 'high' | 'medium' | 'low';
  status: 'open' | 'investigating' | 'resolved';
}
export interface PTPDocument {
  id: string;
  date: string;
  company: string;
  location: string;
  activity: string;
  signatureCount: number;
  totalWorkers: number;
  imageUrls: string[];
}
export interface DataSource {
  id: string;
  type: 'weather' | 'inspection' | 'incident' | 'document' | 'observation' | 'worker' | 'ptp';
  name: string;
  description: string;
  link?: string;
  workers?: InexperiencedWorker[];
  nearMissReports?: NearMissReport[];
  ptpDocument?: PTPDocument;
}
export interface CorrectiveAction {
  id: string;
  action: string;
  priority: 'immediate' | 'today' | 'this_week';
  assignedTo?: string;
}
export interface ContractorContact {
  name: string;
  role: string;
  company: string;
  phone: string;
}
export interface TreatmentRecord {
  treatedBy: string;
  treatedAt: Date;
  notes: string;
}
export interface MeasuresRecord {
  takenBy: string;
  takenAt: Date;
  notes: string;
}
export interface ReopenRecord {
  reopenedBy: string;
  reopenedAt: Date;
  reason: string;
}
export type FactorSeverity = 'high' | 'medium' | 'low' | 'none';
export interface RiskFactorScores {
  weather: FactorSeverity;
  inspections: FactorSeverity;
  workers: FactorSeverity;
  incidents: FactorSeverity;
  documents: FactorSeverity;
  ptpQuality: FactorSeverity;
  ptpEngagement: FactorSeverity;
}
export interface RiskArea {
  id: string;
  project: string;
  floor: string;
  location: string;
  riskLevel: RiskLevel;
  riskScore: number;
  riskFactorScores: RiskFactorScores;
  status: RiskStatus;
  contractors: string[];
  riskFactors: string[];
  dataSources: DataSource[];
  correctiveActions: CorrectiveAction[];
  contacts: ContractorContact[];
  treatment?: TreatmentRecord;
  measuresRecord?: MeasuresRecord;
  reopenHistory?: ReopenRecord[];
  lastUpdated: Date;
}
// Risk band helper — replaces exact score display
export function getRiskBand(score: number): {
  label: string;
  short: string;
  range: string;
} {
  if (score >= 85) return {
    label: 'Extreme',
    short: 'EXT',
    range: '85+'
  };
  if (score >= 70) return {
    label: 'High',
    short: 'HIGH',
    range: '70–84'
  };
  if (score >= 50) return {
    label: 'Moderate',
    short: 'MOD',
    range: '50–69'
  };
  return {
    label: 'Low',
    short: 'LOW',
    range: '<50'
  };
}
export const FACTOR_CONFIG: Record<keyof RiskFactorScores, {
  label: string;
  short: string;
  icon: string;
  description: string;
}> = {
  weather: {
    label: 'Weather',
    short: 'W',
    icon: '🌤',
    description: 'Weather conditions that may increase risk (wind, rain, extreme temperatures)'
  },
  inspections: {
    label: 'Inspections',
    short: 'I',
    icon: '📋',
    description: 'Open inspection findings or overdue inspection items'
  },
  workers: {
    label: 'Workers',
    short: 'Wk',
    icon: '👷',
    description: 'Workforce risk factors (inexperienced workers, crew changes, staffing gaps)'
  },
  incidents: {
    label: 'Incidents',
    short: 'Inc',
    icon: '⚠️',
    description: 'Recent incidents, near-misses, or observations in this area'
  },
  documents: {
    label: 'Documents',
    short: 'D',
    icon: '📄',
    description: 'Missing, expired, or incomplete safety documentation'
  },
  ptpQuality: {
    label: 'PTP Quality',
    short: 'PQ',
    icon: '📝',
    description: 'Quality issues with Pre-Task Plans (vague hazards, missing controls)'
  },
  ptpEngagement: {
    label: 'PTP Engagement',
    short: 'PE',
    icon: '✍️',
    description: 'Low worker participation or sign-off rates on Pre-Task Plans'
  }
};
export const FACTOR_SEVERITY_COLORS: Record<FactorSeverity, {
  dot: string;
  text: string;
  bg: string;
}> = {
  high: {
    dot: 'bg-red-500',
    text: 'text-red-700',
    bg: 'bg-red-100'
  },
  medium: {
    dot: 'bg-amber-400',
    text: 'text-amber-700',
    bg: 'bg-amber-100'
  },
  low: {
    dot: 'bg-emerald-400',
    text: 'text-emerald-700',
    bg: 'bg-emerald-100'
  },
  none: {
    dot: 'bg-gray-200',
    text: 'text-gray-400',
    bg: 'bg-gray-50'
  }
};
const MOCK_RISK_AREAS: RiskArea[] = [
// Card 1: Court Square LIC Floor 12 - weather, workers, ptpEngagement
{
  id: '1',
  project: 'Court Square LIC',
  floor: 'Floor 12',
  location: 'East Wing Scaffolding',
  riskLevel: 'critical',
  riskScore: 92,
  riskFactorScores: {
    weather: 'high',
    inspections: 'none',
    workers: 'high',
    incidents: 'medium',
    documents: 'none',
    ptpQuality: 'none',
    ptpEngagement: 'high'
  },
  status: 'open',
  contractors: ['SteelCo Inc', 'Broadway Construction Group'],
  riskFactors: ['High winds forecasted (35+ mph) for next 48 hours', 'New workers (< 30 days) assigned to scaffolding area', 'Only 8 of 14 workers signed Pre-Task Plan (57% engagement)', 'Scaffolding assembly work at height during wind advisory'],
  dataSources: [{
    id: 'd1',
    type: 'weather',
    name: 'Weather Alert',
    description: 'NWS Wind Advisory for Queens County',
    link: 'https://www.weather.gov/okx/'
  }, {
    id: 'd4',
    type: 'worker',
    name: 'Worker Experience',
    description: '4 workers with < 30 days on site',
    workers: [{
      id: 'w1',
      name: 'James Wilson',
      contractor: 'SteelCo Inc',
      phone: '(555) 111-2222',
      firstDayOnSite: new Date('2026-01-02'),
      daysOnSite: 13
    }, {
      id: 'w2',
      name: 'Maria Garcia',
      contractor: 'SteelCo Inc',
      phone: '(555) 222-3333',
      firstDayOnSite: new Date('2026-01-08'),
      daysOnSite: 7
    }, {
      id: 'w3',
      name: 'David Kim',
      contractor: 'Broadway Construction Group',
      phone: '(555) 333-4444',
      firstDayOnSite: new Date('2025-12-20'),
      daysOnSite: 26
    }, {
      id: 'w4',
      name: 'Ahmed Hassan',
      contractor: 'Broadway Construction Group',
      phone: '(555) 444-5555',
      firstDayOnSite: new Date('2026-01-10'),
      daysOnSite: 5
    }]
  }, {
    id: 'd4a',
    type: 'ptp',
    name: 'Pre-Task Plan',
    description: 'Critical engagement issue: Only 8 of 14 workers signed',
    ptpDocument: {
      id: 'ptp1',
      date: '01/15/2026',
      company: 'SteelCo Inc',
      location: 'Court Square LIC - Floor 12 East Wing',
      activity: 'Scaffolding assembly and installation at height',
      signatureCount: 8,
      totalWorkers: 14,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/hmYR3CQSTsNqCJX9Vr9Zhf/ptp4.jpg', 'https://cdn.magicpatterns.com/uploads/2YzRERvnD5NGi5kqYEad8f/ptp4-2.jpg', 'https://cdn.magicpatterns.com/uploads/hkX6mnj9e1GXruGaGdN9SA/ptp4-3.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a1',
    action: 'Enforce 100% PTP sign-off before work begins',
    priority: 'immediate'
  }, {
    id: 'a2',
    action: 'Restrict scaffolding work during high wind periods',
    priority: 'immediate'
  }, {
    id: 'a3',
    action: 'Assign experienced supervisor to mentor new workers',
    priority: 'today'
  }, {
    id: 'a4',
    action: 'Review fall protection protocols with entire crew',
    priority: 'today'
  }],
  contacts: [{
    name: 'John Smith',
    role: 'Superintendent',
    company: 'Broadway Construction Group',
    phone: '(555) 123-4567'
  }, {
    name: 'Mike Torres',
    role: 'Foreman',
    company: 'SteelCo Inc',
    phone: '(555) 234-5678'
  }],
  lastUpdated: new Date('2026-01-15T08:30:00')
},
// Card 2: 99 Claremont Ave Floor 8 - documents, inspections, ptpQuality
{
  id: '2',
  project: '99 Claremont Ave',
  floor: 'Floor 8',
  location: 'Elevator Shaft Area',
  riskLevel: 'critical',
  riskScore: 88,
  riskFactorScores: {
    weather: 'none',
    inspections: 'medium',
    workers: 'low',
    incidents: 'none',
    documents: 'high',
    ptpQuality: 'medium',
    ptpEngagement: 'none'
  },
  status: 'open',
  contractors: ['AV Mechanic LLC'],
  riskFactors: ['Fall protection equipment expired for 2 workers', "Guardrails reported damaged in yesterday's observation", 'Pre-Task Plan lacks specific fall hazard analysis for elevator shaft work', 'Generic safety checklist used instead of task-specific hazard assessment'],
  dataSources: [{
    id: 'd5',
    type: 'document',
    name: 'Equipment Certifications',
    description: '2 harness certifications expired Jan 12'
  }, {
    id: 'd6',
    type: 'observation',
    name: 'Safety Observation',
    description: 'Damaged guardrail reported Jan 14'
  }, {
    id: 'd7c',
    type: 'ptp',
    name: 'Pre-Task Plan Review',
    description: 'Quality issue: Generic hazard analysis, missing fall-specific controls',
    ptpDocument: {
      id: 'ptp2',
      date: '01/15/2026',
      company: 'AV Mechanic LLC',
      location: '99 Claremont Ave - Floor 8 Elevator Shaft',
      activity: 'Electrical installation near elevator shaft opening',
      signatureCount: 10,
      totalWorkers: 10,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/5LxZz36qyVxaWu4RBxU82a/ptp3-page-001.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a5',
    action: 'Replace expired fall protection equipment immediately',
    priority: 'immediate'
  }, {
    id: 'a6',
    action: 'Repair or replace damaged guardrails',
    priority: 'immediate'
  }, {
    id: 'a7',
    action: 'Redo PTP with specific fall hazard analysis for elevator shaft',
    priority: 'today'
  }, {
    id: 'a8',
    action: 'Conduct toolbox talk on fall protection near openings',
    priority: 'today'
  }],
  contacts: [{
    name: 'Sarah Johnson',
    role: 'SSM',
    company: 'AV Mechanic LLC',
    phone: '(555) 345-6789'
  }],
  lastUpdated: new Date('2026-01-15T07:45:00')
},
// Card 3: 251 Douglass Street Ground - inspections, ptpEngagement, incidents
{
  id: '3',
  project: '251 Douglass Street',
  floor: 'Ground Level',
  location: 'Material Storage Zone',
  riskLevel: 'high',
  riskScore: 74,
  riskFactorScores: {
    weather: 'none',
    inspections: 'high',
    workers: 'none',
    incidents: 'low',
    documents: 'none',
    ptpQuality: 'none',
    ptpEngagement: 'low'
  },
  status: 'open',
  contractors: ['Clear Path Contracting LLC', 'Broadway Construction Group'],
  riskFactors: ['Housekeeping issues noted in 3 consecutive daily inspections', 'Only 6 of 12 forklift operators signed Pre-Task Plan (50% engagement)', 'Near-miss incident: pedestrian nearly struck by forklift yesterday', 'Pedestrian walkways partially blocked by materials'],
  dataSources: [{
    id: 'd8',
    type: 'inspection',
    name: 'Daily Inspections',
    description: 'Housekeeping violations Jan 12-14'
  }, {
    id: 'd10b',
    type: 'incident',
    name: 'Pedestrian Near-Miss',
    description: '1 near-miss with forklift reported',
    nearMissReports: [{
      id: 'nm4',
      title: 'Pedestrian nearly struck by forklift',
      description: 'Worker walking through material storage area nearly struck by reversing forklift. Operator did not see pedestrian due to obstructed walkway. Worker jumped clear at last moment.',
      reportedBy: 'James Rodriguez',
      reportedAt: new Date('2026-01-13T14:20:00'),
      location: 'Ground Level, Material Storage Zone',
      severity: 'high',
      status: 'investigating'
    }]
  }, {
    id: 'd10c',
    type: 'ptp',
    name: 'Pre-Task Plan',
    description: 'Low engagement: Only 6 of 12 operators signed before shift',
    ptpDocument: {
      id: 'ptp3',
      date: '01/15/2026',
      company: 'Clear Path Contracting LLC',
      location: '251 Douglass Street - Ground Level Storage',
      activity: 'Material handling and forklift operations',
      signatureCount: 6,
      totalWorkers: 12,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/hmYR3CQSTsNqCJX9Vr9Zhf/ptp4.jpg', 'https://cdn.magicpatterns.com/uploads/2YzRERvnD5NGi5kqYEad8f/ptp4-2.jpg', 'https://cdn.magicpatterns.com/uploads/hkX6mnj9e1GXruGaGdN9SA/ptp4-3.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a8',
    action: 'Conduct immediate housekeeping sweep',
    priority: 'today'
  }, {
    id: 'a9',
    action: 'Enforce mandatory PTP sign-off before operating equipment',
    priority: 'today'
  }, {
    id: 'a10',
    action: 'Mark and enforce pedestrian walkways',
    priority: 'today'
  }, {
    id: 'a11',
    action: 'Schedule forklift safety refresher training',
    priority: 'this_week'
  }],
  contacts: [{
    name: 'James Rodriguez',
    role: 'Foreman',
    company: 'Clear Path Contracting LLC',
    phone: '(555) 567-8901'
  }, {
    name: 'M. Elizabeth Lagana',
    role: 'SSM',
    company: 'Broadway Construction Group',
    phone: '(555) 678-9012'
  }],
  lastUpdated: new Date('2026-01-15T06:15:00')
},
// Card 4: Court Square LIC Floor 3 - weather, ptpQuality, ptpEngagement
{
  id: '4',
  project: 'Court Square LIC',
  floor: 'Floor 3',
  location: 'Concrete Pour Area',
  riskLevel: 'high',
  riskScore: 71,
  riskFactorScores: {
    weather: 'medium',
    inspections: 'none',
    workers: 'none',
    incidents: 'none',
    documents: 'none',
    ptpQuality: 'medium',
    ptpEngagement: 'medium'
  },
  status: 'open',
  contractors: ['Broadway Construction Group'],
  riskFactors: ['Large concrete pour scheduled for tomorrow (150 cubic yards)', 'Weather forecast shows 60% rain probability', "Pre-Task Plan doesn't address wet weather concrete procedures", 'Only 9 of 14 crew members signed PTP (64% engagement)'],
  dataSources: [{
    id: 'd12',
    type: 'weather',
    name: 'Weather Forecast',
    description: '60% chance of rain, 45°F',
    link: 'https://www.weather.gov/okx/'
  }, {
    id: 'd13b',
    type: 'ptp',
    name: 'Pre-Task Plan',
    description: 'Quality issue: Missing wet weather procedures; 9 of 14 signed',
    ptpDocument: {
      id: 'ptp4',
      date: '01/15/2026',
      company: 'Broadway Construction Group',
      location: 'Court Square LIC - Floor 3',
      activity: 'Concrete pour preparation and placement',
      signatureCount: 9,
      totalWorkers: 14,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/5LxZz36qyVxaWu4RBxU82a/ptp3-page-001.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a11',
    action: 'Update PTP to include wet weather concrete procedures',
    priority: 'today'
  }, {
    id: 'a12',
    action: 'Ensure all crew members sign updated PTP before pour',
    priority: 'today'
  }, {
    id: 'a13',
    action: 'Prepare rain contingency plan and backup schedule',
    priority: 'today'
  }, {
    id: 'a14',
    action: 'Verify all PPE and rain protection equipment',
    priority: 'today'
  }],
  contacts: [{
    name: 'John Smith',
    role: 'Superintendent',
    company: 'Broadway Construction Group',
    phone: '(555) 123-4567'
  }],
  lastUpdated: new Date('2026-01-15T09:00:00')
},
// Card 5: 450 Park Ave Floor 15 - weather, ptpQuality, inspections (CLOSED)
{
  id: '5',
  project: '450 Park Avenue South',
  floor: 'Floor 15',
  location: 'Window Installation Zone',
  riskLevel: 'medium',
  riskScore: 58,
  riskFactorScores: {
    weather: 'medium',
    inspections: 'low',
    workers: 'none',
    incidents: 'none',
    documents: 'none',
    ptpQuality: 'low',
    ptpEngagement: 'none'
  },
  status: 'closed',
  contractors: ['SteelCo Inc'],
  riskFactors: ['Working at height with glass panels during elevated winds', 'Wind speeds approaching 25 mph threshold limit', 'Pre-Task Plan missing wind stop-work criteria and thresholds'],
  dataSources: [{
    id: 'd14',
    type: 'weather',
    name: 'Wind Monitor',
    description: 'Current: 22 mph, Threshold: 25 mph',
    link: 'https://www.weather.gov/okx/'
  }, {
    id: 'd15',
    type: 'inspection',
    name: 'Work Permit',
    description: 'Glass installation permit active'
  }, {
    id: 'd15c',
    type: 'ptp',
    name: 'Pre-Task Plan Review',
    description: 'Quality issue: Missing wind threshold stop-work protocol',
    ptpDocument: {
      id: 'ptp5',
      date: '01/15/2026',
      company: 'SteelCo Inc',
      location: '450 Park Avenue South - Floor 15',
      activity: 'Glass panel installation at height',
      signatureCount: 6,
      totalWorkers: 6,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/hmYR3CQSTsNqCJX9Vr9Zhf/ptp4.jpg', 'https://cdn.magicpatterns.com/uploads/2YzRERvnD5NGi5kqYEad8f/ptp4-2.jpg', 'https://cdn.magicpatterns.com/uploads/hkX6mnj9e1GXruGaGdN9SA/ptp4-3.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a14',
    action: 'Update PTP with specific wind threshold stop-work criteria',
    priority: 'today'
  }, {
    id: 'a15',
    action: 'Monitor wind speeds continuously during work',
    priority: 'today'
  }, {
    id: 'a16',
    action: 'Have backup indoor work ready if wind exceeds threshold',
    priority: 'today'
  }],
  contacts: [{
    name: 'Robert Williams',
    role: 'Superintendent',
    company: 'SteelCo Inc',
    phone: '(555) 890-1234'
  }],
  treatment: {
    treatedBy: 'David Chen',
    treatedAt: new Date('2026-01-15T10:30:00'),
    notes: 'PTP updated with wind thresholds. Wind monitoring protocol activated. Backup tasks assigned. Will reassess at 2pm.'
  },
  lastUpdated: new Date('2026-01-15T10:30:00')
},
// Card 6: 520 Fifth Ave Basement - inspections, workers, ptpEngagement (MEASURES TAKEN)
{
  id: '6',
  project: '520 Fifth Avenue',
  floor: 'Basement',
  location: 'Electrical Room',
  riskLevel: 'medium',
  riskScore: 52,
  riskFactorScores: {
    weather: 'none',
    inspections: 'medium',
    workers: 'medium',
    incidents: 'low',
    documents: 'none',
    ptpQuality: 'none',
    ptpEngagement: 'medium'
  },
  status: 'measures_taken',
  contractors: ['AV Mechanic LLC'],
  riskFactors: ['Confined space work ongoing in electrical room', 'New worker (9 days on site) assigned to confined space entry', 'Only 7 of 10 workers signed confined space PTP (70% engagement)'],
  dataSources: [{
    id: 'd16',
    type: 'inspection',
    name: 'Confined Space Permit',
    description: 'Active permit expires 5pm today'
  }, {
    id: 'd17a',
    type: 'worker',
    name: 'Electricians in Confined Space',
    description: '3 workers in electrical room, 1 new to site',
    workers: [{
      id: 'w15',
      name: 'Thomas Anderson',
      contractor: 'AV Mechanic LLC',
      phone: '(555) 999-1111',
      firstDayOnSite: new Date('2025-10-20'),
      daysOnSite: 87
    }, {
      id: 'w16',
      name: 'Rachel Green',
      contractor: 'AV Mechanic LLC',
      phone: '(555) 000-2222',
      firstDayOnSite: new Date('2025-11-30'),
      daysOnSite: 46
    }, {
      id: 'w17',
      name: 'Steven Wong',
      contractor: 'AV Mechanic LLC',
      phone: '(555) 111-3333',
      firstDayOnSite: new Date('2026-01-06'),
      daysOnSite: 9
    }]
  }, {
    id: 'd17c',
    type: 'ptp',
    name: 'Pre-Task Plan',
    description: 'Engagement issue: Only 7 of 10 workers signed',
    ptpDocument: {
      id: 'ptp6',
      date: '01/15/2026',
      company: 'AV Mechanic LLC',
      location: '520 Fifth Avenue - Basement Electrical Room',
      activity: 'Confined space electrical installation and testing',
      signatureCount: 7,
      totalWorkers: 10,
      imageUrls: ['https://cdn.magicpatterns.com/uploads/5LxZz36qyVxaWu4RBxU82a/ptp3-page-001.jpg']
    }
  }],
  correctiveActions: [{
    id: 'a16',
    action: 'Ensure all workers sign PTP before confined space entry',
    priority: 'today'
  }, {
    id: 'a17',
    action: 'Verify confined space rescue plan with new worker',
    priority: 'today'
  }, {
    id: 'a18',
    action: 'Pair new worker with experienced confined space attendant',
    priority: 'today'
  }],
  contacts: [{
    name: 'Sarah Mitchell',
    role: 'Superintendent',
    company: 'AV Mechanic LLC',
    phone: '(555) 789-0123'
  }],
  measuresRecord: {
    takenBy: 'Sarah Mitchell',
    takenAt: new Date('2026-01-15T09:00:00'),
    notes: 'All workers now signed PTP. Rescue plan verified. New worker paired with experienced attendant. Continuous monitoring in place.'
  },
  lastUpdated: new Date('2026-01-15T07:00:00')
}];
export const RISK_LEVEL_CONFIG: Record<RiskLevel, {
  color: string;
  bgColor: string;
  label: string;
}> = {
  critical: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Extreme'
  },
  high: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'High'
  },
  medium: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    label: 'Moderate'
  },
  low: {
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    label: 'Low'
  }
};
export const STATUS_CONFIG: Record<RiskStatus, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
}> = {
  open: {
    label: 'Open',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: AlertCircle
  },
  measures_taken: {
    label: 'Measures Taken',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: Clock
  },
  closed: {
    label: 'Closed',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    icon: CheckCircle2
  },
  monitoring: {
    label: 'Monitoring',
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    icon: Eye
  }
};
export function RiskAreasWidget({
  fullPage = false,
  initialFilter = null,
  projectFilter = null,
  onClearProjectFilter
}: RiskAreasWidgetProps = {}) {
  const {
    selectedProjects
  } = useProjectFilter();
  const {
    selectedContractors
  } = useContractorFilter();
  const {
    kpis
  } = useKPIs();
  const [selectedRiskArea, setSelectedRiskArea] = useState<EnrichedRiskArea | null>(null);
  const [activeTab, setActiveTab] = useState<'risk_areas' | 'insights' | 'kpis'>('risk_areas');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'measures_taken' | 'closed' | 'monitoring'>(initialFilter === 'open' ? 'open' : 'all');
  const [riskLevelFilter, setRiskLevelFilter] = useState<'all' | 'critical' | 'high'>(initialFilter === 'sif' ? 'critical' : 'all');
  const [showMethodology, setShowMethodology] = useState(false);
  const [methodologyTab, setMethodologyTab] = useState<'formula' | 'tune'>('formula');
  const [savedRiskAreaForMethodology, setSavedRiskAreaForMethodology] = useState<EnrichedRiskArea | null>(null);
  const [riskAreas, setRiskAreas] = useState<EnrichedRiskArea[]>(ENRICHED_MOCK_RISK_AREAS);
  const [listVariant, setListVariant] = useState<'A' | 'B' | 'C' | 'D' | 'E'>('A');
  const [showVariantSwitcher, setShowVariantSwitcher] = useState(false);
  const [futureAlertDismissed, setFutureAlertDismissed] = useState(false);
  const [disclosureLevel, setDisclosureLevel] = useState<0 | 1 | 2>(0);
  const [timeFilter, setTimeFilter] = useState<'today' | 'future' | 'past' | 'all'>('today');
  const [isExpanded, setIsExpanded] = useState(false);
  const allProjectsSelected = selectedProjects?.length === 15;
  const isFiltered = !allProjectsSelected;
  // Apply project filter and sort
  const projectFilteredAreas = riskAreas.filter(area => selectedProjects?.includes(area.project)).filter(area => !projectFilter || area.project === projectFilter).sort((a, b) => {
    // Sort by status first: open → measures_taken → closed
    const statusOrder: Record<string, number> = {
      open: 0,
      measures_taken: 1,
      monitoring: 2,
      closed: 3
    };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    // Then sort by SIF potential
    if (a.sifPotential && !b.sifPotential) return -1;
    if (!a.sifPotential && b.sifPotential) return 1;
    // Then sort by risk score (highest first)
    return b.riskScore - a.riskScore;
  });
  // Apply time filter
  const timeFilteredAreas = projectFilteredAreas.filter(area => {
    if (timeFilter === 'all') return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today);
    dayAfter.setDate(dayAfter.getDate() + 2);
    const areaDate = new Date(area.scheduledDate);
    areaDate.setHours(0, 0, 0, 0);
    if (timeFilter === 'today') {
      return areaDate.getTime() === today.getTime();
    }
    if (timeFilter === 'future') {
      return areaDate.getTime() > today.getTime();
    }
    if (timeFilter === 'past') {
      return areaDate.getTime() < today.getTime();
    }
    return true;
  });
  // Apply status filter and risk level filter
  const statusFilteredAreas = timeFilteredAreas.filter(area => {
    const statusMatch = statusFilter === 'all' ? true : area.status === statusFilter;
    const levelMatch = riskLevelFilter === 'all' ? true : area.riskLevel === riskLevelFilter;
    return statusMatch && levelMatch;
  });
  // Progressive Disclosure Tiers
  // Tier 1: Top 3 hotspots + any SIF items (from current time filter)
  const tier1 = statusFilteredAreas.filter((a, i) => i < 3 || a.sifPotential);
  const tier1Ids = new Set(tier1.map(a => a.id));
  // Tier 2: Remaining medium/high/critical hotspots (from current time filter)
  const tier2 = statusFilteredAreas.filter(a => !tier1Ids.has(a.id) && (a.riskLevel === 'medium' || a.riskLevel === 'high' || a.riskLevel === 'critical'));
  const tier2Ids = new Set(tier2.map(a => a.id));
  // Tier 3: Remaining low-risk activities (respects current time filter)
  const tier12Ids = new Set([...tier1Ids, ...tier2Ids]);
  const tier3 = statusFilteredAreas.filter(a => !tier12Ids.has(a.id));
  let visibleAreas = tier1;
  if (disclosureLevel >= 1) visibleAreas = [...visibleAreas, ...tier2];
  if (disclosureLevel >= 2) visibleAreas = [...visibleAreas, ...tier3];
  const totalItems = visibleAreas.length;
  const handleStatusFilterChange = (filter: 'all' | 'open' | 'measures_taken' | 'closed' | 'monitoring') => {
    setStatusFilter(filter);
    setDisclosureLevel(0); // Reset disclosure when filter changes
  };
  const handleTimeFilterChange = (filter: 'today' | 'future' | 'past' | 'all') => {
    setTimeFilter(filter);
    setDisclosureLevel(0);
  };
  const handleUpdateStatus = (areaId: string, newStatus: RiskStatus, notes: string, userName: string) => {
    setRiskAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const updatedArea = {
        ...area,
        status: newStatus
      };
      if (newStatus === 'closed') {
        updatedArea.treatment = {
          treatedBy: userName,
          treatedAt: new Date(),
          notes
        };
      } else if (newStatus === 'measures_taken') {
        updatedArea.measuresRecord = {
          takenBy: userName,
          takenAt: new Date(),
          notes
        };
      } else if (newStatus === 'open') {
        // Keep the records but update reopen history
        updatedArea.reopenHistory = [...(area.reopenHistory || []), {
          reopenedBy: userName,
          reopenedAt: new Date(),
          reason: notes
        }];
      }
      return updatedArea;
    }));
    // Update selected risk area if it's currently open
    setSelectedRiskArea(prev => {
      if (prev?.id !== areaId) return prev;
      const updatedArea = {
        ...prev,
        status: newStatus
      };
      if (newStatus === 'closed') {
        updatedArea.treatment = {
          treatedBy: userName,
          treatedAt: new Date(),
          notes
        };
      } else if (newStatus === 'measures_taken') {
        updatedArea.measuresRecord = {
          takenBy: userName,
          takenAt: new Date(),
          notes
        };
      } else if (newStatus === 'open') {
        updatedArea.reopenHistory = [...(prev.reopenHistory || []), {
          reopenedBy: userName,
          reopenedAt: new Date(),
          reason: notes
        }];
      }
      return updatedArea;
    });
  };
  // Counts for badges (only counting 'open' items for critical/high)
  const openCriticalCount = timeFilteredAreas.filter(a => a.riskLevel === 'critical' && a.status === 'open').length;
  const openHighCount = timeFilteredAreas.filter(a => a.riskLevel === 'high' && a.status === 'open').length;
  const closedCount = timeFilteredAreas.filter(a => a.status === 'closed').length;
  // Counts for tabs
  const counts = {
    all: timeFilteredAreas.length,
    open: timeFilteredAreas.filter(a => a.status === 'open').length,
    measures_taken: timeFilteredAreas.filter(a => a.status === 'measures_taken').length,
    closed: timeFilteredAreas.filter(a => a.status === 'closed').length,
    monitoring: timeFilteredAreas.filter(a => a.status === 'monitoring').length
  };
  // Time pill indicator logic
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const todayHighRiskCount = projectFilteredAreas.filter(a => {
    const areaDate = new Date(a.scheduledDate);
    areaDate.setHours(0, 0, 0, 0);
    return areaDate.getTime() === todayDate.getTime() && (a.riskLevel === 'critical' || a.riskLevel === 'high') && a.status === 'open';
  }).length;
  const futureHighRiskCount = projectFilteredAreas.filter(a => {
    const areaDate = new Date(a.scheduledDate);
    areaDate.setHours(0, 0, 0, 0);
    return areaDate.getTime() > todayDate.getTime() && (a.riskLevel === 'critical' || a.riskLevel === 'high') && a.status === 'open';
  }).length;
  const openExpanded = (tab: 'risk_areas' | 'insights' | 'kpis' = 'risk_areas', filter?: 'all' | 'open' | 'measures_taken' | 'closed', level?: 'all' | 'critical' | 'high') => {
    setActiveTab(tab);
    if (filter) setStatusFilter(filter);
    if (level) setRiskLevelFilter(level);else setRiskLevelFilter('all');
    setIsExpanded(true);
  };
  const top3OpenRisks = projectFilteredAreas.filter(a => a.status === 'open').slice(0, 3);
  const onTrackCount = kpis.filter(k => k.status === 'on_track').length;
  const offTrackCount = kpis.length - onTrackCount;
  const expandedContent = <div className={`flex flex-col ${fullPage ? 'h-full bg-white rounded-xl shadow-sm border border-gray-200' : 'w-full h-full max-w-[95vw] max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden relative'}`} data-id="element-3433" data-mp="3lunf992ngsoh">
      {/* Expanded Header — only show in modal mode */}
      {!fullPage && <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0" data-id="element-3434" data-mp="21egx9qno5njd">
          <div className="flex items-center gap-3" data-id="element-3435" data-mp="1nj53blhc813i">
            <div className="p-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg" data-id="element-3436" data-mp="1rli0hli0qqoo">
              <AlertTriangle size={20} className="text-white" data-id="element-3437" data-mp="1dlqp7sivzoed" />
            </div>
            <div data-id="element-3438" data-mp="1imeea8nk1v5y">
              <div className="flex items-center gap-2" data-id="element-3439" data-mp="1imwhxqcjrbnw">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-3440" data-mp="14m3m92aieaj7">Risk Areas</h2>
                <div className="flex items-center gap-1 bg-indigo-50 text-indigo-600 text-xs font-semibold px-2 py-0.5 rounded" data-id="element-3441" data-mp="14o6mef5dlk1n">
                  <Sparkles size={12} data-id="element-3442" data-mp="2z0ebvcyxh8q1" />
                  AI
                </div>
              </div>
              <p className="text-sm text-gray-500" data-id="element-3443" data-mp="331gb3tsuhks0">
                Predicted incident hotspots · {projectFilteredAreas.length}{' '}
                activities
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-3444" data-mp="ato6xu7x18pa">
            {isFiltered && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-3445" data-mp="6sb13k4tp5id">
                <Filter size={10} data-id="element-3446" data-mp="pr6uwucrrhvo" />
                Filtered
              </motion.div>}
            <button onClick={() => {
          setMethodologyTab('formula');
          setShowMethodology(true);
        }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="How we estimate risk" data-id="element-3447" data-mp="kslq0hmkoa0w">
              <Info size={18} data-id="element-3448" data-mp="1kt7fxo8t2byv" />
            </button>
            <button onClick={() => setIsExpanded(false)} className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors flex items-center gap-2" title="Close expanded view" data-id="element-3449" data-mp="1kuikhqa2rnr1">
              <Minimize2 size={18} data-id="element-3450" data-mp="1ysqatfclvqt8" />
            </button>
          </div>
        </div>}

      {/* Tabs + utility actions */}
      <div className={`px-6 py-3 border-b border-gray-100 flex-shrink-0 flex items-center justify-between gap-4`} data-id="element-3451" data-mp="23oxjhfwdzgev">
        {!fullPage ? <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-3452" data-mp="2dgl7f6tqifbl">
            <button onClick={() => setActiveTab('risk_areas')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'risk_areas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3453" data-mp="2ify2geuresp9">
              <AlertTriangle size={14} className={activeTab === 'risk_areas' ? 'text-red-500' : 'text-gray-400'} data-id="element-3454" data-mp="3cr1enh9p4h05" />
              Hotspots
            </button>
            <button onClick={() => setActiveTab('insights')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'insights' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3455" data-mp="3mp8mnam0boq0">
              <Sparkles size={14} className={activeTab === 'insights' ? 'text-indigo-600' : 'text-gray-400'} data-id="element-3456" data-mp="2p05gm2encpkg" />
              Company Insights
            </button>
            <button onClick={() => setActiveTab('kpis')} className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${activeTab === 'kpis' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3457" data-mp="2b9sdcdcp9z1g">
              <Target size={14} className={activeTab === 'kpis' ? 'text-emerald-600' : 'text-gray-400'} data-id="element-3458" data-mp="qky9cg4f3w5k" />
              KPIs
            </button>
          </div> : <div className="flex items-center gap-4" data-id="element-3459" data-mp="14k7e8jn6b3u8">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-3460" data-mp="14kh509g1lf0z">Hotspots</h2>
            {projectFilter && <div className="flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-bold px-3 py-1.5 rounded-full border border-indigo-200" data-id="element-3461" data-mp="19i9901tyorlj">
                <Building2 size={12} data-id="element-3462" data-mp="zk38nonsztis" />
                {projectFilter}
                {onClearProjectFilter && <button onClick={onClearProjectFilter} className="ml-1 hover:text-indigo-900 transition-colors" data-id="element-3463" data-mp="3g8fddfedspyr">
                    <X size={12} data-id="element-3464" data-mp="1tpq0xcx5s87p" />
                  </button>}
              </div>}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg" data-id="element-3465" data-mp="1yh7zjubplwpi">
              <button onClick={() => handleTimeFilterChange('past')} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${timeFilter === 'past' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`} data-id="element-3466" data-mp="1kpthbppmlzji">
                <Calendar size={12} className={timeFilter === 'past' ? 'text-indigo-200' : ''} data-id="element-3467" data-mp="2heizlh560u60" />
                Past
              </button>
              <button onClick={() => handleTimeFilterChange('today')} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${timeFilter === 'today' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`} data-id="element-3468" data-mp="28e2yufft414u">
                <Calendar size={12} className={timeFilter === 'today' ? 'text-indigo-200' : ''} data-id="element-3469" data-mp="pikh6wjt6afv" />
                Today
                {todayHighRiskCount > 0 && <span className="bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full" data-id="element-3470" data-mp="ti1nttaz1dmm">
                    {todayHighRiskCount}
                  </span>}
              </button>
              <button onClick={() => handleTimeFilterChange('future')} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${timeFilter === 'future' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`} data-id="element-3471" data-mp="1r590n5coywp">
                <Calendar size={12} className={timeFilter === 'future' ? 'text-indigo-200' : ''} data-id="element-3472" data-mp="3j5skyp1a0upq" />
                Future
                {futureHighRiskCount > 0 && <span className="bg-red-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full" data-id="element-3473" data-mp="1i31gazyc8y6w">
                    {futureHighRiskCount}
                  </span>}
              </button>
              <button onClick={() => handleTimeFilterChange('all')} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all flex items-center gap-1.5 ${timeFilter === 'all' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`} data-id="element-3474" data-mp="1rxbcksqeigmw">
                <Calendar size={12} className={timeFilter === 'all' ? 'text-indigo-200' : ''} data-id="element-3475" data-mp="1947n7jk7upy3" />
                All
              </button>
            </div>
          </div>}

        {/* Utility actions alongside tabs */}
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3476" data-mp="2oky2s8wvt2rw">
          {isFiltered && fullPage && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-3477" data-mp="cye2ug1w90f6">
              <Filter size={10} data-id="element-3478" data-mp="3qcr7n1yajzrr" />
              Filtered
            </motion.div>}
          {fullPage && <button onClick={() => {
          setMethodologyTab('formula');
          setShowMethodology(true);
        }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="How we estimate risk" data-id="element-3479" data-mp="3ld66qlzk9hce">
              <Info size={18} data-id="element-3480" data-mp="1fjelbjhecls8" />
            </button>}
          <span className="text-xs text-gray-400" data-id="element-3481" data-mp="1alwb7prn80in">
            {projectFilteredAreas.length} areas
          </span>
        </div>
      </div>

      {!fullPage && activeTab === 'insights' ? <div className="flex-1 overflow-y-auto p-6" data-id="element-3482" data-mp="c9tnf5uqsbdc">
          <InsightsTab data-id="element-3483" data-mp="c9a7tvskdz2h" />
        </div> : !fullPage && activeTab === 'kpis' ? <div className="flex-1 overflow-y-auto p-6" data-id="element-3484" data-mp="3uedjjzd1d9bv">
          <KPIsTab data-id="element-3485" data-mp="393vwecil75f" />
        </div> : <>
          {/* Summary badges as filters */}
          <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0" data-id="element-3486" data-mp="3kplq1msbugyu">
            <div className="flex gap-2 flex-wrap" data-id="element-3487" data-mp="3kniu6m4httm7">
              {openCriticalCount > 0 && <button onClick={() => {
            if (riskLevelFilter === 'critical') {
              setRiskLevelFilter('all');
              setStatusFilter('all');
            } else {
              setRiskLevelFilter('critical');
              setStatusFilter('open');
            }
            setDisclosureLevel(0);
          }} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${riskLevelFilter === 'critical' ? 'bg-red-100 border-2 border-red-400 text-red-800 ring-1 ring-red-300' : 'bg-red-50 border border-red-200 text-red-700 hover:bg-red-100'}`} data-id="element-3488" data-mp="3piezgnyqqdh9">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-3489" data-mp="2s2m4cv7pnddm" />
                  {openCriticalCount} Extreme
                </button>}
              {openHighCount > 0 && <button onClick={() => {
            if (riskLevelFilter === 'high') {
              setRiskLevelFilter('all');
              setStatusFilter('all');
            } else {
              setRiskLevelFilter('high');
              setStatusFilter('open');
            }
            setDisclosureLevel(0);
          }} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${riskLevelFilter === 'high' ? 'bg-orange-100 border-2 border-orange-400 text-orange-800 ring-1 ring-orange-300' : 'bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100'}`} data-id="element-3490" data-mp="314lmjqugoni5">
                  <span className="w-2 h-2 bg-orange-500 rounded-full" data-id="element-3491" data-mp="5lpwe9go4ms0" />
                  {openHighCount} High
                </button>}
              {counts.measures_taken > 0 && <button onClick={() => {
            if (statusFilter === 'measures_taken') {
              setStatusFilter('all');
            } else {
              setStatusFilter('measures_taken');
            }
            setRiskLevelFilter('all');
            setDisclosureLevel(0);
          }} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${statusFilter === 'measures_taken' ? 'bg-amber-100 border-2 border-amber-400 text-amber-800 ring-1 ring-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100'}`} data-id="element-3492" data-mp="ei7b1mo8r2si">
                  <Clock size={12} data-id="element-3493" data-mp="17h2ny04h22hi" />
                  {counts.measures_taken} In Progress
                </button>}
              {closedCount > 0 && <button onClick={() => {
            if (statusFilter === 'closed') {
              setStatusFilter('all');
            } else {
              setStatusFilter('closed');
            }
            setRiskLevelFilter('all');
            setDisclosureLevel(0);
          }} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${statusFilter === 'closed' ? 'bg-emerald-100 border-2 border-emerald-400 text-emerald-800 ring-1 ring-emerald-300' : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'}`} data-id="element-3494" data-mp="yjsemkb7snf8">
                  <CheckCircle2 size={12} data-id="element-3495" data-mp="25uhye434ybmo" />
                  {closedCount} Closed
                </button>}
              {counts.monitoring > 0 && <button onClick={() => {
            if (statusFilter === 'monitoring') {
              setStatusFilter('all');
            } else {
              setStatusFilter('monitoring');
            }
            setRiskLevelFilter('all');
            setDisclosureLevel(0);
          }} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${statusFilter === 'monitoring' ? 'bg-sky-100 border-2 border-sky-400 text-sky-800 ring-1 ring-sky-300' : 'bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100'}`} data-id="element-3496" data-mp="1vyoqiy0d36b8">
                  <Eye size={12} data-id="element-3497" data-mp="3ccey4eqowcpm" />
                  {counts.monitoring} Monitoring
                </button>}
              {(statusFilter !== 'all' || riskLevelFilter !== 'all') && <button onClick={() => {
            setStatusFilter('all');
            setRiskLevelFilter('all');
            setDisclosureLevel(0);
          }} className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded-full hover:bg-gray-100 transition-colors ml-1" data-id="element-3498" data-mp="vybijxp3yzvb">
                  <X size={12} data-id="element-3499" data-mp="czqteb6s24kb" />
                  Clear filter
                </button>}
            </div>
          </div>

          {/* Risk Areas List */}
          <div className="flex-1 overflow-y-auto pb-24" data-id="element-3500" data-mp="mx4xrcfff2w9">
            {fullPage ? <div className="flex flex-col min-h-full" data-id="element-3501" data-mp="mz7uefb33u9y">
                <AnimatePresence data-id="element-3502" data-mp="22cuceq4si9m2">
                  {timeFilter === 'today' && futureHighRiskCount > 0 && !futureAlertDismissed && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="mx-6 mt-4 mb-2 bg-red-50 border-2 border-red-300 rounded-lg p-3 flex items-start gap-3 shadow-sm" data-id="element-3503" data-mp="2czeec91zq4ad">
                        <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" data-id="element-3504" data-mp="2lz9teoyo2jfo" />
                        <div className="flex-1" data-id="element-3505" data-mp="2gym3k6m6c8n1">
                          <h4 className="text-sm font-bold text-red-900" data-id="element-3506" data-mp="3egvwth256dq2">
                            ⚠ Upcoming High-Risk Activities
                          </h4>
                          <p className="text-xs text-red-700 mt-0.5" data-id="element-3507" data-mp="3hxrs9ylqz879">
                            There {futureHighRiskCount === 1 ? 'is' : 'are'}{' '}
                            {futureHighRiskCount} critical/high risk{' '}
                            {futureHighRiskCount === 1 ? 'activity' : 'activities'}{' '}
                            scheduled in the coming days.
                            <button onClick={() => handleTimeFilterChange('future')} className="ml-1 font-bold underline hover:text-red-800" data-id="element-3508" data-mp="4q6szvei4vam">
                              Review now →
                            </button>
                          </p>
                        </div>
                        <button onClick={() => setFutureAlertDismissed(true)} className="flex-shrink-0 text-red-400 hover:text-red-600 transition-colors p-0.5 rounded hover:bg-red-100" data-id="element-3509" data-mp="nhzktmhu4agr">
                          <X size={14} data-id="element-3510" data-mp="2sydpdesqns9" />
                        </button>
                      </motion.div>}
                </AnimatePresence>
                <div className="flex-1" data-id="element-3511" data-mp="ciq9gaymtmyb">
                  {listVariant === 'A' && <VariantATable areas={visibleAreas} onSelect={setSelectedRiskArea} data-id="element-3512" data-mp="7r899p4fnqee" />}
                  {listVariant === 'B' && <VariantBList areas={visibleAreas} onSelect={setSelectedRiskArea} data-id="element-3513" data-mp="gj1w1tak1xcf" />}
                  {listVariant === 'C' && <VariantCCards areas={visibleAreas} onSelect={setSelectedRiskArea} data-id="element-3514" data-mp="qisqxw312ed1" />}
                  {listVariant === 'D' && <VariantDSplit areas={visibleAreas} onSelect={setSelectedRiskArea} data-id="element-3515" data-mp="2sbpay8ex0r9a" />}
                  {listVariant === 'E' && <VariantEKanban areas={visibleAreas} onSelect={setSelectedRiskArea} data-id="element-3516" data-mp="y1s8dw6xhvtr" />}
                </div>

                {/* Progressive Disclosure Controls */}
                <div className="py-8 flex flex-col items-center justify-center relative" data-id="element-3517" data-mp="p0avemotia9a">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-3518" data-mp="t3qnr6tutog6">
                    <div className="w-full border-t border-gray-200" data-id="element-3519" data-mp="f5971t2d9c7r"></div>
                  </div>

                  {disclosureLevel === 0 && tier2.length > 0 && <motion.button initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} onClick={() => setDisclosureLevel(1)} className="relative bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm flex items-center gap-2" data-id="element-3520" data-mp="29hgtsj4jamsg">
                      Show {tier2.length} more hotspots{' '}
                      <ChevronDown size={16} data-id="element-3521" data-mp="3mtuel4z1jqrs" />
                    </motion.button>}

                  {disclosureLevel === 1 && tier3.length > 0 && <motion.button initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} onClick={() => setDisclosureLevel(2)} className="relative bg-white text-gray-500 px-4 py-1.5 rounded-full text-xs font-medium hover:text-gray-700 transition-colors flex items-center gap-1" data-id="element-3522" data-mp="3hsex1mw2o2e5">
                      Show all activities ({tier3.length} more){' '}
                      <ChevronDown size={14} data-id="element-3523" data-mp="1t90yta0cskca" />
                    </motion.button>}

                  {disclosureLevel > 0 && <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} onClick={() => setDisclosureLevel(0)} className={`relative bg-white text-gray-400 px-4 py-1.5 rounded-full text-xs font-medium hover:text-gray-600 transition-colors ${disclosureLevel === 1 && tier3.length > 0 ? 'mt-2' : ''}`} data-id="element-3524" data-mp="10n93ogl7nfcm">
                      Show less
                    </motion.button>}
                </div>
              </div> : statusFilteredAreas.length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-3525" data-mp="1k8346y1olv7y">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-3526" data-mp="1yyacwm1vzzvg">
                  <CheckCircle2 size={32} className="text-emerald-500" data-id="element-3527" data-mp="1tzz48enbvvet" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-3528" data-mp="16aei5mvvoi1b">
                  No activities found
                </h3>
                <p className="text-gray-500 max-w-sm" data-id="element-3529" data-mp="11c393edeqbk5">
                  Try adjusting your filters
                </p>
              </div> : <div className="divide-y divide-gray-100" data-id="element-3530" data-mp="1b86ldd1z3qkt">
                {visibleAreas.map((area, index) => {
            const config = RISK_LEVEL_CONFIG[area.riskLevel];
            const statusConfig = STATUS_CONFIG[area.status];
            const StatusIcon = statusConfig.icon;
            const band = getRiskBand(area.riskScore);
            const activeFactors = (Object.entries(area.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, severity]) => severity !== 'none').sort((a, b) => {
              const order = {
                high: 0,
                medium: 1,
                low: 2,
                none: 3
              };
              return order[a[1]] - order[b[1]];
            }).slice(0, 3);
            let rowBg = 'hover:bg-gray-50';
            if (area.status === 'closed') {
              rowBg = 'bg-emerald-50/30 opacity-75 hover:bg-emerald-50/50';
            } else if (area.status === 'measures_taken') {
              rowBg = 'bg-amber-50/30 hover:bg-amber-50/50';
            }
            return <motion.button key={area.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: index * 0.02
            }} onClick={() => setSelectedRiskArea(area)} className={`w-full text-left px-6 py-3.5 flex items-center gap-4 transition-colors group ${rowBg}`} data-id="element-3531" data-mp="20mru7izccqcq" data-mpi={index}>
                      {/* Risk score band */}
                      <div className={`flex-shrink-0 w-14 py-1.5 rounded-md ${config.bgColor} flex flex-col items-center`} title={`Risk: ${band.short} (${band.range})`} data-id="element-3532" data-mp="25ka0t2pvythk">
                        <span className={`text-xs font-bold ${config.color}`} data-id="element-3533" data-mp="u3a0bjtdx8cg">
                          {band.short}
                        </span>
                        <span className={`text-[9px] ${config.color} opacity-70`} data-id="element-3534" data-mp="ozreq1e6wbqi">
                          {band.range}
                        </span>
                      </div>

                      {/* Location info */}
                      <div className="flex-1 min-w-0" data-id="element-3535" data-mp="yrx5j7spgn44">
                        <div className="flex items-center gap-1.5 mb-0.5" data-id="element-3536" data-mp="12qe2wzmbqj8c">
                          <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-3537" data-mp="17pzcs5318bzu">
                            {area.project}
                          </span>
                          <ChevronRight size={12} className="text-gray-300 flex-shrink-0" data-id="element-3538" data-mp="1cnitdbn7d0uy" />
                          <span className="text-sm text-gray-600 truncate" data-id="element-3539" data-mp="3eijbtqe7zkk3">
                            {area.floor}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate" data-id="element-3540" data-mp="1rfqpel1e97xh">
                          {area.location}
                        </p>
                      </div>

                      {/* Risk level + status badges */}
                      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3541" data-mp="20e2nt98i5a0h">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${config.bgColor} ${config.color}`} data-id="element-3542" data-mp="256vsjx3z0ick">
                          {config.label}
                        </span>
                        <span className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded ${statusConfig.bgColor} ${statusConfig.color}`} data-id="element-3543" data-mp="2j64z052djy3c">
                          <StatusIcon size={10} data-id="element-3544" data-mp="osnhayonhvg8" />
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Contributing factors */}
                      <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0" data-id="element-3545" data-mp="m7pkvp1qkndm">
                        {activeFactors.map(([factor, severity]) => {
                  const factorConf = FACTOR_CONFIG[factor];
                  const sevColors = FACTOR_SEVERITY_COLORS[severity];
                  return <div key={factor} className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${sevColors.bg}`} title={`${factorConf.label}: ${severity}`} data-id="element-3546" data-mp="7gglio817nna">
                              <span className={`w-1.5 h-1.5 rounded-full ${sevColors.dot}`} data-id="element-3547" data-mp="v6ax72gm765y" />
                              <span className={`text-[10px] font-medium ${sevColors.text}`} data-id="element-3548" data-mp="3fyux9985ogt4">
                                {factorConf.label}
                              </span>
                            </div>;
                })}
                      </div>

                      {/* Contractors */}
                      <div className="hidden xl:flex items-center gap-1 text-xs text-gray-400 flex-shrink-0 max-w-[160px]" data-id="element-3549" data-mp="33g92kzoffj3l">
                        <Users size={11} className="flex-shrink-0" data-id="element-3550" data-mp="38divwqexsewp" />
                        <span className="truncate" data-id="element-3551" data-mp="2umvwsx59kn03">
                          {area.contractors.join(', ')}
                        </span>
                      </div>

                      {/* Chevron */}
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0" data-id="element-3552" data-mp="2kq108jsjwwog" />
                    </motion.button>;
          })}
              </div>}
          </div>

          {/* Expanded Footer */}
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500 flex-shrink-0" data-id="element-3553" data-mp="2fs975mg674io">
            <span data-id="element-3554" data-mp="1s3b1t81lhm58">
              Showing {visibleAreas.length} of {projectFilteredAreas.length}{' '}
              activities · Updated{' '}
              {new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
            </span>
            {isFiltered && <span className="flex items-center gap-1 text-amber-600 font-medium" data-id="element-3555" data-mp="2fzfgiqm2qgnh">
                <Filter size={12} data-id="element-3556" data-mp="2b2f8ia5nz3tq" />
                Filtered by project
              </span>}
          </div>
        </>}

      {/* Variant Switcher */}
      {fullPage && <div className="fixed bottom-6 left-6 z-[9999] flex flex-col items-start" data-id="element-3557" data-mp="2kr6wk77lt3zd">
          <AnimatePresence data-id="element-3558" data-mp="2yqygspoprglt">
            {showVariantSwitcher && <motion.div initial={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} className="mb-3 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden flex flex-col w-48" data-id="element-3559" data-mp="2yp3wndl9nsis">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3560" data-mp="2uw5cy9es3lgw">
                  List Variants
                </div>
                {(['A', 'B', 'C', 'D', 'E'] as const).map(v => <button key={v} onClick={() => {
            setListVariant(v);
            setShowVariantSwitcher(false);
          }} className={`px-4 py-2.5 text-sm text-left transition-colors flex items-center justify-between ${listVariant === v ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-3561" data-mp="38v553r1i2iht">
                    <span data-id="element-3562" data-mp="23owwpgetwzrp">
                      {v === 'A' && 'A: Dense Table'}
                      {v === 'B' && 'B: Compact List'}
                      {v === 'C' && 'C: Card Grid'}
                      {v === 'D' && 'D: Split Pane'}
                      {v === 'E' && 'E: Kanban'}
                    </span>
                    {listVariant === v && <CheckCircle2 size={14} data-id="element-3563" data-mp="1pue0jmphashu" />}
                  </button>)}
              </motion.div>}
          </AnimatePresence>
          <button onClick={() => setShowVariantSwitcher(!showVariantSwitcher)} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-full shadow-lg hover:bg-gray-800 transition-colors font-medium text-sm" data-id="element-3564" data-mp="1fzcb0prajoeq">
            <Settings2 size={16} data-id="element-3565" data-mp="35dh3j4qo87w3" />
            Variant {listVariant}
          </button>
        </div>}
    </div>;
  if (fullPage) {
    return <>
        {expandedContent}
        {/* Detail Modal */}
        <RiskAreaDetailModal riskArea={selectedRiskArea} onClose={() => setSelectedRiskArea(null)} onUpdateStatus={handleUpdateStatus} onOpenMethodology={() => {
        setSavedRiskAreaForMethodology(selectedRiskArea);
        setSelectedRiskArea(null);
        setMethodologyTab('tune');
        setShowMethodology(true);
      }} data-id="element-3566" data-mp="18f1p0nm4ev5u" />

        {/* Methodology Modal */}
        <AnimatePresence data-id="element-3567" data-mp="1rd48ynzta1ws">
          {showMethodology && <RiskMethodologyModal onClose={() => {
          setShowMethodology(false);
          if (savedRiskAreaForMethodology) {
            setSelectedRiskArea(savedRiskAreaForMethodology);
            setSavedRiskAreaForMethodology(null);
          }
        }} initialTab={methodologyTab} data-id="element-3568" data-mp="1deebv8czs28f" />}
        </AnimatePresence>
      </>;
  }
  return <>
      <Card className="h-full flex flex-col" data-id="element-3569" data-mp="3ukblmi8vqgfn">
        {/* Header */}
        <div className="flex items-start justify-between mb-4" data-id="element-3570" data-mp="3pm1q26j8tdti">
          <div className="flex items-center gap-2" data-id="element-3571" data-mp="8eyajkv4fe0d">
            <div className="p-1.5 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg" data-id="element-3572" data-mp="8eg2zs30ddd5">
              <AlertTriangle size={16} className="text-white" data-id="element-3573" data-mp="3h6fwj33ddsh" />
            </div>
            <div data-id="element-3574" data-mp="3buvgfhm91zsw">
              <div className="flex items-center gap-2" data-id="element-3575" data-mp="3bwgbd8ch7kra">
                <h3 className="font-bold text-gray-900" data-id="element-3576" data-mp="36wu219zrqonr">Risk Areas</h3>
                <div className="flex items-center gap-1 bg-indigo-50 text-indigo-600 text-[10px] font-semibold px-1.5 py-0.5 rounded" data-id="element-3577" data-mp="36uhcobrolc0o">
                  <Sparkles size={10} data-id="element-3578" data-mp="3gu8424ky3awj" />
                  AI
                </div>
              </div>
              <p className="text-xs text-gray-500" data-id="element-3579" data-mp="2t4niddxepzjk">
                Predicted incident hotspots
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-id="element-3580" data-mp="2o5u77s5lekmk">
            {isFiltered && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-3581" data-mp="22oc5hwvsfrfa">
                <Filter size={10} data-id="element-3582" data-mp="2ll3w6rdnt8td" />
                Filtered
              </motion.div>}
            <button onClick={() => setIsExpanded(true)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Expand risk areas" data-id="element-3583" data-mp="2qj5gffmtne6f">
              <Maximize2 size={16} data-id="element-3584" data-mp="34c4vxhmlt8dz" />
            </button>
            <button onClick={() => {
            setMethodologyTab('formula');
            setShowMethodology(true);
          }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="How we estimate risk" data-id="element-3585" data-mp="34dpoj1pcyh97">
              <Info size={16} data-id="element-3586" data-mp="sqvv3ba49pm1" />
            </button>
          </div>
        </div>

        {/* Summary badges (clickable) */}
        <div className="flex gap-2 mb-4" data-id="element-3587" data-mp="jocy37t0i77p">
          {openCriticalCount > 0 && <button onClick={() => openExpanded('risk_areas', 'open', 'critical')} className="flex items-center gap-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors" data-id="element-3588" data-mp="jqftk7cxosjt">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" data-id="element-3589" data-mp="17jpdb4grwjue" />
              {openCriticalCount} Extreme
            </button>}
          {openHighCount > 0 && <button onClick={() => openExpanded('risk_areas', 'open', 'high')} className="flex items-center gap-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors" data-id="element-3590" data-mp="204ftvjfoyk7l">
              <span className="w-2 h-2 bg-orange-500 rounded-full" data-id="element-3591" data-mp="1j5wca7pmaqtk" />
              {openHighCount} High
            </button>}
          {counts.measures_taken > 0 && <button onClick={() => openExpanded('risk_areas', 'measures_taken')} className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full transition-colors" data-id="element-3592" data-mp="1s2ww011qs0qo">
              <Clock size={12} data-id="element-3593" data-mp="2fsraso5mfzdk" />
              {counts.measures_taken} In Progress
            </button>}
          {closedCount > 0 && <button onClick={() => openExpanded('risk_areas', 'closed')} className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full ml-auto transition-colors" data-id="element-3594" data-mp="2ps9rmvq8j51e">
              <CheckCircle2 size={12} data-id="element-3595" data-mp="38gahj5b6q7h1" />
              {closedCount} Closed
            </button>}
        </div>

        {/* Top 3 Risks */}
        <div className="flex-1 space-y-2 mb-4 overflow-y-auto min-h-0" data-id="element-3596" data-mp="2ug9f3lf9urzg">
          {top3OpenRisks.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-center py-8" data-id="element-3597" data-mp="2zhyt99xsjlrr">
              <CheckCircle2 size={40} className="text-emerald-400 mb-3" data-id="element-3598" data-mp="2kpoha07f9rhl" />
              <p className="text-sm font-medium text-gray-900" data-id="element-3599" data-mp="2pihqb09xblzh">
                No active risks found
              </p>
            </div> : top3OpenRisks.map(area => {
          const config = RISK_LEVEL_CONFIG[area.riskLevel];
          const band = getRiskBand(area.riskScore);
          return <button key={area.id} onClick={() => openExpanded('risk_areas')} className="w-full text-left p-2.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all flex items-center gap-3 bg-white group" data-id="element-3600" data-mp="21ugb58i8p7b3">
                  <div className={`flex-shrink-0 w-10 rounded-md ${config.bgColor} flex flex-col items-center justify-center py-1 px-1`} data-id="element-3601" data-mp="1dwintspcm3xm">
                    <span className={`text-[8px] font-bold uppercase tracking-wide ${config.color}`} data-id="element-3602" data-mp="18vky8v20e7m3">
                      {band.short}
                    </span>
                    <span className={`text-[7px] ${config.color} opacity-70`} data-id="element-3603" data-mp="21kieytqknwcq">
                      {band.range}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-3604" data-mp="1rnnfa7ikbpwv">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-3605" data-mp="l7uzfo9ayyfu">
                      <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`} data-id="element-3606" data-mp="l60azzytoo70">
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-medium text-gray-900" data-id="element-3607" data-mp="2wvq0taa9jwgi">
                      <span className="truncate" data-id="element-3608" data-mp="31t8bp67uclr5">{area.project}</span>
                      <ChevronRight size={10} className="text-gray-300 flex-shrink-0" data-id="element-3609" data-mp="36mb7ll03c7ai" />
                      <span className="truncate text-gray-600" data-id="element-3610" data-mp="3flxyhcv71upb">
                        {area.floor}
                      </span>
                    </div>
                  </div>

                  <ChevronRight size={14} className="text-gray-300 group-hover:text-gray-400 transition-colors flex-shrink-0" data-id="element-3611" data-mp="3kiy4xovygzgy" />
                </button>;
        })}
        </div>

        {/* Teasers */}
        <div className="space-y-2 mb-4 flex-shrink-0" data-id="element-3612" data-mp="1v6mhwlvgcu60">
          <button onClick={() => openExpanded('insights')} className="w-full flex items-center justify-between p-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors text-sm font-medium group" data-id="element-3613" data-mp="3lmos6firbq4x">
            <div className="flex items-center gap-2" data-id="element-3614" data-mp="3ulsc1lpr09kh">
              <Sparkles size={14} data-id="element-3615" data-mp="3qncit032q4ym" />
              <span data-id="element-3616" data-mp="3qn2oj0azxbn4">5 new insights available</span>
            </div>
            <ChevronRight size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3617" data-mp="32xrxwbnd0xmp" />
          </button>
          <button onClick={() => openExpanded('kpis')} className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-colors text-sm font-medium group ${offTrackCount === 0 ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700' : offTrackCount >= Math.ceil(kpis.length / 2) ? 'bg-red-50 hover:bg-red-100 text-red-700' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`} data-id="element-3618" data-mp="3bp0oh036b8am">
            <div className="flex items-center gap-2" data-id="element-3619" data-mp="252xmhhs36zjk">
              <Target size={14} data-id="element-3620" data-mp="251cunzx5pupe" />
              <span data-id="element-3621" data-mp="29ufng5u4104j">
                {onTrackCount} of {kpis.length} KPIs on track
                {offTrackCount > 0 && <span className={`ml-1 ${offTrackCount >= Math.ceil(kpis.length / 2) ? 'text-red-600' : 'text-amber-600'}`} data-id="element-3622" data-mp="1v6chgwg2hofq">
                    · {offTrackCount} need attention
                  </span>}
              </span>
            </div>
            <ChevronRight size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-3623" data-mp="2srgz29i65yhg" />
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 flex-shrink-0" data-id="element-3624" data-mp="2itav7mcsyi9y">
          <span className="text-xs text-gray-500" data-id="element-3625" data-mp="12esud1gcnq8g">
            Updated{' '}
            {new Date().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
          </span>
          <button onClick={() => openExpanded('risk_areas')} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors" data-id="element-3626" data-mp="3a46zx03cb070">
            View all areas →
          </button>
        </div>
      </Card>

      {/* Detail Modal */}
      <RiskAreaDetailModal riskArea={selectedRiskArea} onClose={() => setSelectedRiskArea(null)} onUpdateStatus={handleUpdateStatus} onOpenMethodology={() => {
      setSavedRiskAreaForMethodology(selectedRiskArea);
      setSelectedRiskArea(null);
      setMethodologyTab('tune');
      setShowMethodology(true);
    }} data-id="element-3627" data-mp="2vcy4ubgo4mmh" />

      {/* Expanded Fullscreen Modal */}
      <AnimatePresence data-id="element-3628" data-mp="2cgothvx5ia66">
        {isExpanded && <div className="fixed inset-0 z-50 flex items-center justify-center p-6" data-id="element-3629" data-mp="fl0v5u5mejfx">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsExpanded(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-3630" data-mp="ampjrezog0vl" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          duration: 0.4,
          bounce: 0.2
        }} className="relative w-full h-full max-w-[95vw] max-h-[90vh] bg-transparent" onClick={e => e.stopPropagation()} data-id="element-3631" data-mp="ryntb6ya9r0o">
              {expandedContent}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Methodology Modal */}
      <AnimatePresence data-id="element-3632" data-mp="2ap5nzacg108r">
        {showMethodology && <RiskMethodologyModal onClose={() => {
        setShowMethodology(false);
        if (savedRiskAreaForMethodology) {
          setSelectedRiskArea(savedRiskAreaForMethodology);
          setSavedRiskAreaForMethodology(null);
        }
      }} initialTab={methodologyTab} data-id="element-3633" data-mp="25p17vlfg67j2" />}
      </AnimatePresence>
    </>;
}